// Org self-service + compliance endpoints (admin JWT). Shapes mirror the API:
//   - profile   → api/src/services/org-profile-service.ts (GET /admin/org)
//   - rotate    → api/src/services/org-id-service.ts       (POST /admin/org/rotate)
//   - export    → api/src/services/gdpr-service.ts         (GET /admin/gdpr/export)
//   - erase     → api/src/services/gdpr-service.ts         (POST /admin/gdpr/erase)
//
// Identity-bearing / drift-prone responses are Zod-validated at the boundary
// (vault Type_Assertion) so envelope drift fails loud as a ContractError.
import { z } from "zod";
import { apiFetch } from "./client";
import type { TierId } from "./auth";

export interface Org {
  id: string;
  name: string;
  orgScope: string;
  tier: TierId;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrgProfile {
  org: Org;
  terms: {
    /** Version currently in force. */
    current: string;
    /** Version THIS admin accepted (null for ops-provisioned / pre-T&C). */
    accepted: string | null;
    acceptedAt: string | null;
  };
}

const orgSchema = z.object({
  id: z.string(),
  name: z.string(),
  orgScope: z.string(),
  tier: z.enum(["free", "small", "medium", "large", "xlarge"]),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const orgProfileSchema = z.object({
  org: orgSchema,
  terms: z.object({
    current: z.string(),
    accepted: z.string().nullable(),
    acceptedAt: z.string().nullable(),
  }),
});

export function getOrgProfile(signal?: AbortSignal): Promise<OrgProfile> {
  return apiFetch("/admin/org", { auth: true, signal, schema: orgProfileSchema });
}

// --- Org rename (OWNER-only server-side — a non-owner admin gets a 403 the UI
//     surfaces). Returns the refreshed profile. ---
export function renameOrg(name: string): Promise<OrgProfile> {
  return apiFetch("/admin/org", { method: "PATCH", auth: true, body: { name }, schema: orgProfileSchema });
}

// --- Re-accept the current Terms (stamps the caller's own acceptance). Returns
//     the refreshed profile so the "you accepted version X" line updates. ---
export function acceptTerms(): Promise<OrgProfile> {
  return apiFetch("/admin/org/accept-terms", { method: "POST", auth: true, schema: orgProfileSchema });
}

// --- Org_ID rotation: returns a NEW plaintext Org_ID ONCE (like the welcome reveal). ---
export interface RotateResult {
  orgId: string;
  org: Org;
}
const rotateSchema = z.object({ orgId: z.string(), org: orgSchema });

export function rotateOrgId(): Promise<RotateResult> {
  return apiFetch("/admin/org/rotate", { method: "POST", auth: true, schema: rotateSchema });
}

// --- GDPR export: the org's personal data (no secrets). Loose by design — arrays of
//     opaque rows handed straight to the user as a downloaded file, so no schema. ---
export interface OrgExport {
  org: Record<string, unknown> | null;
  admins: Record<string, unknown>[];
  channels: Record<string, unknown>[];
  auditLog: Record<string, unknown>[];
  slackMappings: Record<string, unknown>[];
  exportedAt: string;
}

export function exportOrg(signal?: AbortSignal): Promise<OrgExport> {
  return apiFetch<OrgExport>("/admin/gdpr/export", { auth: true, signal });
}

// --- GDPR erase: OWNER-only + the org name typed back as confirmation (the API
//     rejects a mismatch BEFORE anything is deleted). Irreversible. ---
export interface EraseResult {
  erased: boolean;
  relayChannelsDeleted: number;
  relayChannelsFailed: number;
}
const eraseSchema = z.object({
  erased: z.boolean(),
  relayChannelsDeleted: z.number(),
  relayChannelsFailed: z.number(),
});

export function eraseOrg(confirmation: string): Promise<EraseResult> {
  return apiFetch("/admin/gdpr/erase", { method: "POST", auth: true, body: { confirmation }, schema: eraseSchema });
}
